import { cn } from "@/lib/utils";
import type { MappingTier } from "@/types/analytics";

/** Tiers supported by the badge. Extends `MappingTier` with a "Manual" slot for user interventions. */
export type TierBadgeTier = MappingTier | "Manual";

interface TierBadgeProps {
  tier: TierBadgeTier;
  /** Override the default label. */
  label?: string;
  size?: "sm" | "md";
  className?: string;
}

/**
 * Small pill showing which mapping tier produced a result.
 *
 * Colours intentionally lean traffic-light — Green is the klyra brand green,
 * not a generic success green, so the pill reinforces the product taxonomy
 * rather than "ok/bad" semantics.
 */
export function TierBadge({ tier, label, size = "sm", className }: TierBadgeProps) {
  const display = label ?? tier;

  const toneClass =
    tier === "Green"
      ? "bg-[var(--color-brand-primary)]/15 text-[var(--color-brand-primary-dark)] border-[var(--color-brand-primary)]/30"
      : tier === "Amber"
        ? "bg-[var(--color-warning-light)] text-[#854F0B] border-[var(--color-warning)]/40"
        : tier === "Red"
          ? "bg-[var(--color-error-light)] text-[#791F1F] border-[var(--color-error)]/40"
          : tier === "Manual"
            ? "bg-[#E6F1FB] text-[#0C447C] border-[#85B7EB]/60"
            : "bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] border-[var(--color-border)]";

  const sizeClass = size === "sm" ? "text-[10px] px-2 py-0.5" : "text-[11px] px-2.5 py-1";

  return (
    <span
      className={cn(
        "inline-flex items-center font-semibold rounded-full border uppercase tracking-wide whitespace-nowrap",
        sizeClass,
        toneClass,
        className
      )}
    >
      {display}
    </span>
  );
}
